import { ExternalLink, CheckCircle, Loader2 } from "lucide-react"
import { Button } from "@/modules/shared/ui/components/button"
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from "@/modules/shared/ui/components/dialog"

interface Careers24Result {
    success: boolean
    message?: string
    vacancyUrl?: string
}

interface Careers24ResultModalProps {   
    open: boolean
    onOpenChange: (open: boolean) => void
    result: Careers24Result | null
    isLoading?: boolean
}

export function Careers24ResultModal({
    open,
    onOpenChange,
    result,
    isLoading = false,
}: Careers24ResultModalProps) {
    return (
        <Dialog
            open={open}
            onOpenChange={(value) => {
                if (isLoading) return
                onOpenChange(value)
            }}
        >
            <DialogContent
                className="sm:max-w-md"
                onInteractOutside={(e) => {
                    if (isLoading) e.preventDefault()
                }}
                onEscapeKeyDown={(e) => {
                    if (isLoading) e.preventDefault()
                }}
            >
                {isLoading && (
                    <div className="flex flex-col items-center justify-center py-10 gap-4">
                        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                        <p className="text-sm text-muted-foreground">
                            Posting vacancy to Careers24...
                        </p>
                    </div>
                )}

                {!isLoading && result && (
                    <>
                        <DialogHeader>
                            <DialogTitle className="flex items-center gap-2 text-green-700">
                                <CheckCircle className="h-5 w-5 text-green-600" />
                                {result.message ?? "Vacancy Posted Successfully"}
                            </DialogTitle>
                        </DialogHeader>

                        <div className="py-4 space-y-3">
                            <p className="text-sm text-muted-foreground">
                                Your vacancy has been successfully posted to Careers24.
                            </p>
                            {result.vacancyUrl && (
                                <div className="flex flex-col gap-1">
                                    <span className="text-sm font-medium text-foreground">
                                        View your listing:
                                    </span>
                                    <a
                                        href={result.vacancyUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center gap-1.5 text-sm text-primary hover:text-primary/80 hover:underline break-all"
                                    >
                                        {result.vacancyUrl}
                                        <ExternalLink className="h-3.5 w-3.5 shrink-0" />
                                    </a>
                                </div>
                            )}
                        </div>

                        <DialogFooter>
                            <Button variant="outline" onClick={() => onOpenChange(false)}>
                                Close
                            </Button>
                            {result.vacancyUrl && (
                                <Button
                                    className="bg-primary hover:bg-primary/90 text-primary-foreground"
                                    onClick={() => window.open(result.vacancyUrl, "_blank")}
                                >
                                    <ExternalLink className="h-4 w-4 mr-2" />
                                    Open on Careers24
                                </Button>
                            )}
                        </DialogFooter>
                    </>
                )}
            </DialogContent>
        </Dialog>
    )
}